import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Lead, LeadFilters } from '../../types/Lead';
import { StorageUtil } from '../../utils/storage';
import { Company } from '../../types/Company';
import { DashboardViewMode, StarredLead } from '../../types/Star';
import { filterLeads } from '../../services/leadService';
import { starService } from '../../services/starService';
import { memberService } from '../../services/memberService';
import { findCompaniesService } from '../../services/findCompaniesService';

import { useAuth } from '../../contexts/AuthContext';
import { useLeads } from '../../contexts/LeadsContext';
import { useSearch } from '../../hooks/useSearch';
import { useSortedLeads } from '../../hooks/useSortedLeads';

import TopMenu from './TopMenu';
import { StarredLeadsView } from './StarredLeadsView';
import { MyLeadsView } from './MyLeadsView';
import GenerateLeadView from './GenerateLeadView';
import FindCompaniesView from './FindCompaniesView';
import LeadCard from '../leads/LeadCard';
import { Pagination } from '../ui/Pagination';
import FirebaseStatus from '../firebase/FirebaseStatus';
import { leadGenerationStatusService } from '../../services/leadGenerationStatusService';

const FILTERS_STORAGE_KEY = 'dashboard.currentFilters';

/** Whether any lead-applicable filter (company size, sector, geography) is set. */
const hasActiveLeadFilters = (filters: LeadFilters): boolean =>
  Boolean(
    (filters.companySize && filters.companySize.length > 0) ||
    (filters.sector && filters.sector.length > 0) ||
    (filters.geography && filters.geography.length > 0)
  );

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { leads, stats, loading, error, updateLeadInCache } = useLeads();
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);

  // Apply custom scoring and sort leads using custom hook
  const sortedLeads = useSortedLeads(leads);
  const sortedFilteredLeads = useSortedLeads(filteredLeads);

  // Search functionality
  const {
    searchQuery,
    filteredLeads: searchFilteredLeads,
    isSearching,
    resultsCount,
    setSearchQuery,
    clearSearch
  } = useSearch(sortedFilteredLeads);

  // Apply custom scoring and sort search results
  const sortedSearchFilteredLeads = useSortedLeads(searchFilteredLeads);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // New state for view management
  const [currentView, setCurrentView] = useState<DashboardViewMode>('all');
  const [starredLeadIds, setStarredLeadIds] = useState<Set<string>>(new Set());
  const [starredLeads, setStarredLeads] = useState<StarredLead[]>([]);
  const [memberLeadIds, setMemberLeadIds] = useState<Set<string>>(new Set()); // leads with any member
  const [myLeadIds, setMyLeadIds] = useState<Set<string>>(new Set()); // leads where current user is assigned

  const [viewCounts, setViewCounts] = useState({
    all: 0,
    starred: 0,
    member: 0,
    leadsWithMembers: 0,
    generate: 0,
    findcompanies: 0
  });
  const [companiesCount, setCompaniesCount] = useState(0);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [currentFilters, setCurrentFilters] = useState<LeadFilters>(
    () => StorageUtil.getJSON<LeadFilters>(FILTERS_STORAGE_KEY) ?? {}
  );

  // Persist filter selections so they survive switching views/navigation.
  useEffect(() => {
    StorageUtil.setJSON(FILTERS_STORAGE_KEY, currentFilters);
  }, [currentFilters]);

  const leadsGeneratedCompanies = useMemo(
    () => new Set(sortedLeads.map(l => l.metadata.companyName.toLowerCase())),
    [sortedLeads],
  );

  const companyStats = useMemo(() => ({
    sectors: Array.from(new Set(allCompanies.map(c => c.sector).filter(Boolean) as string[])).sort(),
    geographies: Array.from(new Set(allCompanies.map(c => c.geography).filter(Boolean) as string[])).sort(),
    industries: Array.from(new Set(allCompanies.map(c => c.industry).filter(Boolean) as string[])).sort(),
  }), [allCompanies]);

  // Ref to track if filtered leads have been initialized
  const filteredLeadsInitialized = useRef(false);

  // Pagination state for all views
  const [currentViewPage, setCurrentViewPage] = useState<Record<DashboardViewMode, number>>({
    all: 1,
    starred: 1,
    member: 1,
    generate: 1,
    findcompanies: 1
  });



  // Load starred status for current user
  const loadStarredStatus = useCallback(async () => {
    if (!user) {
      setStarredLeadIds(new Set());
      setStarredLeads([]);
      return;
    }

    try {
      const starredLeadsData = await starService.getStarredLeads(user.uid);
      const starredIds = new Set(starredLeadsData.map(star => star.leadId));
      setStarredLeadIds(starredIds);
      setStarredLeads(starredLeadsData);
    } catch (err) {
      console.error('Failed to load starred status:', err);
      // Don't clear the existing starred state on error, just log it
    }
  }, [user]);

  // Load member status using two single Firestore queries (fast)
  const loadMemberStatus = useCallback(async () => {
    if (!user) {
      setMemberLeadIds(new Set());
      setMyLeadIds(new Set());
      return;
    }

    try {
      const [allLeadsWithMembers, myLeads] = await Promise.all([
        memberService.getAllLeadsWithMembers(),
        memberService.getLeadsForMember(user.uid),
      ]);
      setMemberLeadIds(new Set(allLeadsWithMembers));
      setMyLeadIds(new Set(myLeads));
    } catch (err) {
      console.error('Failed to load member status:', err);
    }
  }, [user]);

  // Calculate view counts
  const calculateViewCounts = useCallback(async () => {
    if (!user || leads.length === 0) {
      setViewCounts({ all: leads.length, starred: 0, member: 0, leadsWithMembers: 0, generate: 0, findcompanies: companiesCount });
      return;
    }

    try {
      let starredCount = starredLeadIds.size;
      let myLeadsCount = myLeadIds.size;
      let leadsWithMembersCount = memberLeadIds.size;
      let userLeadsCount = leads.filter(lead => lead.userId === user?.uid).length;

      if (filteredLeadsInitialized.current && filteredLeads.length > 0) {
        starredCount = filteredLeads.filter(lead =>
          lead.id && starredLeadIds.has(lead.id)
        ).length;

        myLeadsCount = filteredLeads.filter(lead =>
          lead.id && myLeadIds.has(lead.id)
        ).length;

        leadsWithMembersCount = filteredLeads.filter(lead =>
          lead.id && memberLeadIds.has(lead.id)
        ).length;

        userLeadsCount = filteredLeads.filter(lead =>
          lead.userId === user?.uid
        ).length;
      }

      const allLeadsCount = filteredLeadsInitialized.current ? filteredLeads.length : leads.length;

      setViewCounts({
        all: allLeadsCount,
        starred: starredCount,
        member: myLeadsCount,
        leadsWithMembers: leadsWithMembersCount,
        generate: userLeadsCount,
        findcompanies: companiesCount
      });
    } catch (err) {
      console.error('❌ Dashboard: Failed to calculate view counts:', err);
      setViewCounts({ all: leads.length, starred: 0, member: 0, leadsWithMembers: 0, generate: 0, findcompanies: companiesCount });
    }
  }, [user, leads, filteredLeads, filteredLeadsInitialized, starredLeadIds, memberLeadIds, myLeadIds, companiesCount]);

  // Reset filteredLeads when the user logs out (leads are cleared by the context)
  useEffect(() => {
    if (!user) {
      setFilteredLeads([]);
      filteredLeadsInitialized.current = false;
    }
  }, [user]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentViewPage(prev => ({
      ...prev,
      [currentView]: 1
    }));
  }, [searchQuery, currentView]);

  // Subscribe to real-time updates for lead generation status
  useEffect(() => {
    if (!user) {
      return;
    }

    // Subscribe to lead generation status updates
    // This ensures real-time updates work for both manual generation and file uploads
    const unsubscribe = leadGenerationStatusService.subscribeToUserAttempts(
      user.uid,
      () => {
        // Real-time status updates are handled by LeadGenerationAttemptsList directly
      }
    );

    return unsubscribe;
  }, [user]);

  // Initialize filtered leads with sorted leads when they're available,
  // re-applying any persisted filter selections so they survive navigation.
  useEffect(() => {
    if (leads.length > 0) {
      // Only set filtered leads if they haven't been initialized yet
      setFilteredLeads(prev => {
        if (prev.length === 0) {
          if (currentView !== 'findcompanies' && hasActiveLeadFilters(currentFilters)) {
            filteredLeadsInitialized.current = true;
            return filterLeads(sortedLeads, currentFilters);
          }
          filteredLeadsInitialized.current = true;
          return sortedLeads;
        }
        return prev;
      });
    } else {
      // Handle empty leads state
      setFilteredLeads([]);
      filteredLeadsInitialized.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, sortedLeads]);

  // Load starred status when user or leads change
  useEffect(() => {
    if (leads.length > 0) {
      loadStarredStatus();
    } else {
      // Clear starred state when no leads
      setStarredLeadIds(new Set());
    }
  }, [loadStarredStatus, leads.length]);

  // Load member status when user changes
  useEffect(() => {
    loadMemberStatus();
  }, [loadMemberStatus]);



  // Load companies on startup
  useEffect(() => {
    if (!user) return;
    findCompaniesService.loadCompanies(user.uid)
      .then(companies => {
        setAllCompanies(companies);
        setCompaniesCount(companies.length);
      })
      .catch(err => console.error('Failed to load companies count:', err));
  }, [user]);

  // Calculate view counts when leads, user, or attempts change
  useEffect(() => {
    calculateViewCounts();
  }, [calculateViewCounts]);

  // Recalculate view counts when filtered leads change
  useEffect(() => {
    calculateViewCounts();
  }, [filteredLeads, calculateViewCounts]);

    const handleFiltersChange = (filters: LeadFilters) => {
    setCurrentFilters(filters);

    if (currentView === 'findcompanies') return;

    if (sortedLeads.length === 0) {
      setFilteredLeads([]);
      filteredLeadsInitialized.current = false;
      return;
    }

    // If no filters are applied, reset to show all leads
    if (!hasActiveLeadFilters(filters)) {
      setFilteredLeads(sortedLeads);
      filteredLeadsInitialized.current = false;
    } else {
      const filtered = filterLeads(sortedLeads, filters);
      setFilteredLeads(filtered);
      filteredLeadsInitialized.current = true;
    }

    // Reset pagination to first page when filters change
    setCurrentViewPage(prev => ({
      ...prev,
      [currentView]: 1
    }));

    // View counts will be recalculated automatically when filteredLeads changes
  };

  const toggleCard = (leadId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(leadId)) {
      newExpanded.delete(leadId);
    } else {
      newExpanded.add(leadId);
    }
    setExpandedCards(newExpanded);
  };

  const handleLeadUpdate = (updatedLead: Lead) => {
    updateLeadInCache(updatedLead);
    setFilteredLeads(prev => prev.map(lead => lead.id === updatedLead.id ? updatedLead : lead));
  };

  // Handle star changes
  const handleStarChange = useCallback((leadId: string, isStarred: boolean) => {
    if (isStarred) {
      setStarredLeadIds(prev => {
        const newSet = new Set(prev);
        newSet.add(leadId);
        return newSet;
      });

      // Add to starredLeads state
      setStarredLeads(prev => {
        const newStarredLead: StarredLead = {
          leadId,
          starredAt: new Date().toISOString(),
          notes: undefined,
          priority: 'medium',
          starId: '' // This will be set when the actual star is created
        };
        return [...prev, newStarredLead];
      });
    } else {
      setStarredLeadIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });

      // Remove from starredLeads state
      setStarredLeads(prev => prev.filter(star => star.leadId !== leadId));
    }

    // Recalculate view counts
    calculateViewCounts();
  }, [calculateViewCounts]);

  const handleCompaniesLoaded = useCallback((companies: Company[]) => {
    setAllCompanies(companies);
    setCompaniesCount(companies.length);
    if (user) findCompaniesService.updateCache(user.uid, companies);
  }, [user]);

  // Handle member changes
  const handleMemberChange = useCallback((leadId: string, isMember: boolean) => {
    // isMember = current user was added/removed
    setMyLeadIds(prev => {
      const newSet = new Set(prev);
      isMember ? newSet.add(leadId) : newSet.delete(leadId);
      return newSet;
    });
    // memberLeadIds tracks leads with any member — when current user is added,
    // the lead now has at least one member; removal doesn't mean no members remain,
    // so reload from server to keep it accurate.
    if (isMember) {
      setMemberLeadIds(prev => {
        const newSet = new Set(prev);
        newSet.add(leadId);
        return newSet;
      });
    } else {
      // Reload to check if any other members remain
      memberService.getAllLeadsWithMembers()
        .then(ids => setMemberLeadIds(new Set(ids)))
        .catch(err => console.error('Failed to reload member lead ids:', err));
    }
    calculateViewCounts();
  }, [calculateViewCounts]);

  // Handle pagination changes for different views
  const handlePageChange = useCallback((page: number, view: DashboardViewMode) => {
    setCurrentViewPage(prev => ({
      ...prev,
      [view]: page
    }));
  }, []);

  // Handle view change
  const handleViewChange = (view: DashboardViewMode) => {
    setCurrentView(view);
    setExpandedCards(new Set()); // Collapse all cards when switching views

    // Reset pagination for the new view to page 1
    setCurrentViewPage(prev => ({
      ...prev,
      [view]: 1
    }));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leads...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      );
    }

    // Show empty state when no leads exist, but only for the 'all' view
    // Other views (starred, member, generate) should handle their own empty states
    if ((!leads || leads.length === 0) && currentView === 'all') {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '800px',
            minHeight: '220px',
            backgroundColor: 'rgba(34, 197, 94, 0.05)',
            borderRadius: '8px',
            padding: '24px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '2px solid #bbf7d0',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              opacity: 0.3,
            }}>
              🔍
            </div>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              margin: 0,
            }}>
              No leads generated yet
            </h3>
            <p style={{
              color: '#6b7280',
              fontSize: '16px',
              lineHeight: '1.5',
              margin: 0,
              maxWidth: '300px',
            }}>
              Please generate leads from the{' '}
              <button
                onClick={() => handleViewChange('generate')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0ea5e9',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  padding: 0,
                  margin: 0,
                  fontWeight: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0284c7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#0ea5e9';
                }}
              >
                Generate Lead
              </button>
              {' '}tab to get started.
            </p>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'starred':
        return (
          <StarredLeadsView
            allLeads={sortedFilteredLeads}
            onLeadToggle={toggleCard}
            expandedCards={expandedCards}
            onMemberChange={handleMemberChange}
            onStarChange={handleStarChange}
            starredLeadIds={starredLeadIds}
            starredLeads={starredLeads}
            currentPage={currentViewPage.starred}
            onPageChange={(page) => handlePageChange(page, 'starred')}
            totalPages={Math.ceil(sortedFilteredLeads.filter(lead => starredLeadIds.has(lead.id || '')).length / 10)}
            itemsPerPage={10}
          />
        );
      case 'member':
        return (
          <MyLeadsView
            allLeads={sortedFilteredLeads}
            onLeadToggle={toggleCard}
            expandedCards={expandedCards}
            memberLeadIds={myLeadIds}
            onMemberChange={handleMemberChange}
            onStarChange={handleStarChange}
            currentPage={currentViewPage.member}
            onPageChange={(page) => handlePageChange(page, 'member')}
            totalPages={Math.ceil(sortedFilteredLeads.filter(lead => lead.id && myLeadIds.has(lead.id)).length / 10)}
            itemsPerPage={10}
          />
        );
      case 'generate':
        return (
          <GenerateLeadView
            allLeads={sortedFilteredLeads}
            onLeadToggle={toggleCard}
            expandedCards={expandedCards}
            onLeadUpdate={handleLeadUpdate}
            starredLeadIds={starredLeadIds}
            onStarChange={handleStarChange}
            onMemberChange={handleMemberChange}
            currentPage={currentViewPage.generate}
            onPageChange={(page) => handlePageChange(page, 'generate')}
            totalPages={Math.ceil(sortedFilteredLeads.filter(lead => lead.userId === user?.uid).length / 10)}
            itemsPerPage={10}
          />
        );
      case 'findcompanies':
        return (
          <FindCompaniesView
            onCompaniesLoaded={handleCompaniesLoaded}
            initialCompanies={allCompanies}
            sectorFilter={currentFilters.sector}
            geographyFilter={currentFilters.geography}
            industryFilter={currentFilters.industry}
            searchQuery={searchQuery}
            leadsGeneratedCompanies={leadsGeneratedCompanies}
          />
        );
      default: // 'all'
        // Use search-filtered leads or regular filtered leads (both are already sorted)
        const displayLeads = isSearching ? sortedSearchFilteredLeads : sortedFilteredLeads;
        const paginatedLeads = displayLeads.slice((currentViewPage.all - 1) * 10, currentViewPage.all * 10);

        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {isSearching ? 'Search Results' : 'All Leads - Ranked by AI Score'}
              </h2>
              <p className="text-gray-600">
                {isSearching ? (
                  <>
                    Found {resultsCount} result{resultsCount !== 1 ? 's' : ''} for "{searchQuery}"
                    {resultsCount > 0 && (
                      <button
                        onClick={clearSearch}
                        style={{
                          marginLeft: '12px',
                          color: '#0ea5e9',
                          textDecoration: 'underline',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'inherit'
                        }}
                      >
                        Clear search
                      </button>
                    )}
                  </>
                ) : (
                  `Showing ${displayLeads.length} of ${leads.length} leads, sorted by custom weighted score`
                )}
              </p>
            </div>

            <div className="space-y-6">
              {paginatedLeads.map((lead, index) => (
                <div key={`${lead.id || lead.metadata.companyName}-${index}`} className="animate-fade-in">
                  <LeadCard
                    lead={lead}
                    isExpanded={expandedCards.has(lead.id || lead.metadata.companyName)}
                    onToggle={() => toggleCard(lead.id || lead.metadata.companyName)}
                    onLeadUpdate={handleLeadUpdate}
                    isStarred={lead.id ? starredLeadIds.has(lead.id) : false}
                    onStarChange={(isStarred) => lead.id && handleStarChange(lead.id, isStarred)}
                    onMemberChange={handleMemberChange}
                  />
                </div>
              ))}
            </div>

                          {/* Pagination */}
              {displayLeads.length > 0 && (
                <div style={{ marginTop: '48px' }}>
                  <Pagination
                    currentPage={currentViewPage.all}
                    totalPages={Math.ceil(displayLeads.length / 10)}
                    onPageChange={(page) => handlePageChange(page, 'all')}
                    totalItems={displayLeads.length}
                    itemsPerPage={10}
                  />
                </div>
              )}

            {displayLeads.length === 0 && (
              <div className="text-center py-8">
                <div style={{ color: '#9ca3af', marginBottom: '16px' }}>
                  <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ margin: '0 auto' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                      isSearching
                        ? "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        : "M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"
                    } />
                  </svg>
                </div>
                <p className="text-gray-600">
                  {isSearching
                    ? `No leads found matching "${searchQuery}". Try a different search term.`
                    : 'No leads match your current filters.'
                  }
                </p>
                {isSearching && (
                  <button
                    onClick={clearSearch}
                    style={{
                      marginTop: '12px',
                      padding: '8px 16px',
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen">
      <TopMenu
        stats={stats}
        companyStats={companyStats}
        filters={currentFilters}
        onFiltersChange={handleFiltersChange}
        onSearchChange={setSearchQuery}
        searchQuery={searchQuery}
        currentView={currentView}
        onViewChange={handleViewChange}
        viewCounts={viewCounts}
      />

      <div className="py-8" style={{
        width: (currentView === 'generate' || currentView === 'findcompanies') ? '100%' : '60%',
        margin: '0 auto',
        padding: (currentView === 'generate' || currentView === 'findcompanies') ? '0' : '20px 24px'
      }}>
        {renderContent()}
      </div>

      <FirebaseStatus />
    </div>
  );
};

export default Dashboard;